import { Link } from "react-router-dom";
import { Clock, ShieldAlert, Lightbulb, Trash2 } from "lucide-react";
import type { Analysis } from "@erc/shared";

interface AnalysisCardProps {
  analysis: Analysis;
  onDelete?: () => void;
}

const CP_BADGE: Record<string, { label: string; cls: string }> = {
  low: { label: "Düşük", cls: "bg-green-100 text-green-700" },
  medium: { label: "Orta", cls: "bg-amber-100 text-amber-700" },
  high: { label: "Yüksek", cls: "bg-red-100 text-red-700" },
};

export function AnalysisCard({ analysis, onDelete }: AnalysisCardProps) {
  const riskNotes = Array.isArray(analysis.riskNotes) ? analysis.riskNotes : [];
  const highRisks = riskNotes.filter((r) => r.severity === "high");
  const briefCount = analysis.ideaBriefs?.length ?? 0;
  const thumb = analysis.source?.imageUrls?.[0];
  const cp = analysis.copyright ? (CP_BADGE[analysis.copyright.level] ?? CP_BADGE.low) : null;

  return (
    <Link
      to={`/analyses/${analysis.id}`}
      className="block rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow group"
    >
      <div className="flex items-start gap-3 mb-3">
        {thumb && (
          <img src={thumb} alt="" loading="lazy" className="h-14 w-14 flex-shrink-0 rounded-lg border border-gray-100 object-cover" />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 break-words group-hover:text-indigo-600 transition-colors">
            {analysis.source?.title ?? analysis.productType}
          </h3>
          <p className="mt-0.5 text-sm text-gray-500 break-words">
            {analysis.niche}
          </p>
        </div>
        {onDelete && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
            title="Analizi sil"
            className="flex-shrink-0 rounded p-1 text-gray-300 hover:bg-red-50 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {analysis.suggestedDescription && (
        <p className="mb-3 text-sm text-gray-600 leading-snug line-clamp-3">
          {analysis.suggestedDescription}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700">
          {analysis.productType}
        </span>
        {cp && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${cp.cls}`}>
            <ShieldAlert className="h-3 w-3" /> Telif: {cp.label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Lightbulb className="h-3.5 w-3.5 text-indigo-400" />
          <span>{briefCount} brief fikri</span>
        </div>
        {highRisks.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <ShieldAlert className="h-3.5 w-3.5" />
            <span>{highRisks.length} yüksek risk</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-400">
        <Clock className="h-3 w-3" />
        <span>{new Date(analysis.createdAt).toLocaleDateString("tr-TR")}</span>
      </div>
    </Link>
  );
}
